import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, FileCheck, Wallet, CalendarDays, MapPin, Briefcase, Clock, Banknote } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { getCountry } from "../data/countries";

export default function CountryGuide() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const c = getCountry(slug);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!c) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <div className="max-w-[760px] mx-auto px-5 py-24 text-center flex-1">
          <h1 className="font-head text-2xl font-bold text-[#16244F]">Guide not found</h1>
          <button onClick={() => navigate("/destinations")} className="mt-5 text-[#F58220] font-head font-semibold">All destinations</button>
        </div>
        <Footer />
      </div>
    );
  }

  const Card = ({ icon: Icon, title, children }) => (
    <div className="bg-white rounded-2xl cc-shadow-card p-6">
      <h3 className="font-head text-lg font-bold text-[#16244F] flex items-center gap-2 mb-4"><Icon size={20} className="text-[#2b8cf0]" /> {title}</h3>
      {children}
    </div>
  );
  const Row = ({ label, value }) => (
    <div className="flex justify-between gap-4 py-2 border-b border-gray-50 last:border-0">
      <span className="text-sm text-[#8a90a0]">{label}</span>
      <span className="text-sm text-[#16244F] font-medium text-right">{value}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f6f8fc] flex flex-col">
      <Header />
      <main className="flex-1">
        <section className="relative h-[280px] md:h-[360px] overflow-hidden">
          <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#16244F] via-[#16244F]/55 to-transparent" />
          <div className="absolute inset-0 max-w-[1240px] mx-auto px-5 flex flex-col justify-end pb-8">
            <button onClick={() => navigate("/destinations")} className="inline-flex items-center gap-1.5 text-white/80 hover:text-white font-head text-sm mb-4 w-fit"><ArrowLeft size={16} /> All destinations</button>
            <h1 className="font-head text-4xl md:text-5xl font-bold text-white flex items-center gap-3"><span className="text-4xl">{c.flag}</span> {c.name}</h1>
            <p className="text-white/85 mt-2 max-w-xl">{c.tagline}</p>
          </div>
        </section>

        <section className="max-w-[1240px] mx-auto px-5 py-10 md:py-14 grid grid-cols-1 md:grid-cols-2 gap-6">
          {c.domestic ? (
            <Card icon={FileCheck} title="Admissions">
              <Row label="Entry type" value={c.admissions.type} />
              <Row label="Key exams" value={c.admissions.exams} />
              <Row label="Eligibility" value={c.admissions.eligibility} />
              <Row label="How to apply" value={c.admissions.process} />
            </Card>
          ) : (
            <Card icon={FileCheck} title="Visa Essentials">
              <Row label="Visa type" value={c.visa.type} />
              <Row label="Processing time" value={c.visa.processing} />
              <Row label="Proof of funds" value={c.visa.funds} />
              <Row label="Work rights" value={c.visa.work} />
            </Card>
          )}
          <Card icon={Wallet} title="Cost of Study & Living">
            <Row label="Tuition" value={c.cost.tuition} />
            <Row label="Living cost" value={c.cost.living} />
            <Row label="Note" value={c.cost.city} />
          </Card>
          <Card icon={CalendarDays} title="Intakes">
            <div className="flex flex-wrap gap-2">
              {c.intakes.map((i) => (
                <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#eaf3ff] text-[#16244F] text-sm font-medium"><Clock size={14} className="text-[#2b8cf0]" /> {i}</span>
              ))}
            </div>
            <h4 className="font-head font-semibold text-[#16244F] mt-5 mb-2 flex items-center gap-2"><MapPin size={16} className="text-[#F58220]" /> Popular cities</h4>
            <div className="flex flex-wrap gap-2">
              {c.popularCities.map((city) => (
                <span key={city} className="px-3 py-1 rounded-full bg-[#f6f8fc] text-sm text-[#5a6274]">{city}</span>
              ))}
            </div>
          </Card>
          <Card icon={Briefcase} title="After You Graduate">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#16a34a]/10 text-[#16a34a] flex items-center justify-center shrink-0"><Banknote size={20} /></div>
              <p className="text-[#45506b]">{c.postStudy}</p>
            </div>
            <button onClick={() => navigate("/", { state: { scrollTo: "contact" } })} className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#F58220] hover:bg-[#e0730f] text-white font-head font-semibold py-3 rounded-lg transition-colors">Get a Free Consultation</button>
          </Card>
        </section>
      </main>
      <Footer />
    </div>
  );
}
